
import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from "@/components/ui/toaster";

// Public routes
import LandingPage from "./pages/LandingPage";
import Login from "./pages/Login";
import Register from "./pages/Register";

// Dashboard routes
import DashboardLayout from "./layouts/DashboardLayout";
import Dashboard from "./pages/dashboard/Dashboard";
import PagesManager from "./pages/dashboard/PagesManager";
import YahrzeitManager from "./pages/dashboard/YahrzeitManager";
import Settings from "./pages/dashboard/Settings";
import UsersManager from "./pages/dashboard/UsersManager";
import TeamManagement from "./pages/dashboard/TeamManagement";
import TimesManagerPage from "./pages/dashboard/TimesManagerPage";
import ZmanimPage from "./components/pages/ZmanimPage";

// Yahrzeit public form
import PublicForm from "./pages/yahrzeit/PublicForm";

// Error and status pages
import NotFound from "./pages/NotFound";
import Unauthorized from "./pages/Unauthorized";
import AccountSuspended from "./pages/AccountSuspended";
import SubscriptionRequired from "./pages/SubscriptionRequired";

// Admin routes
import SuperUserDashboard from "./pages/admin/SuperUserDashboard";
import UsersAdmin from "./pages/admin/UsersAdmin";
import AdminPanel from "./pages/AdminPanel";

// Context Providers
import { SupabaseProvider } from "./contexts/SupabaseContext";
import { AuthProvider } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { SettingsProvider } from "./contexts/SettingsContext";
import { PageProvider } from "./contexts/PageContext";
import { SuperUserEditingBanner } from "./components/SuperUserEditingBanner";

// Auth helpers
import { RoleProtectedRoute } from "./components/RoleProtectedRoute";
import DisplayBoard from "./pages/DisplayBoard";

const queryClient = new QueryClient();

function App() {
  return (
    <SupabaseProvider>
      <AuthProvider>
        <LanguageProvider>
          <SettingsProvider>
            <QueryClientProvider client={queryClient}>
              <Toaster />
              <SuperUserEditingBanner />
              <Routes>
                {/* Public routes (Marketing Site) */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                
                {/* Public Yahrzeit Form */}
                <Route path="/yahrzeit/public/:userId" element={<PublicForm />} />
                
                {/* Status/Error pages */}
                <Route path="/unauthorized" element={<Unauthorized />} />
                <Route path="/account-suspended" element={<AccountSuspended />} />
                <Route path="/subscription-required" element={<SubscriptionRequired />} />
                <Route path="*" element={<NotFound />} />
                
                {/* Dashboard routes (Protected) */}
                <Route path="/dashboard" element={
                  <PageProvider>
                    <DashboardLayout />
                  </PageProvider>
                }>
                  <Route index element={<Dashboard />} />
                  <Route path="pages" element={<PagesManager />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="team" element={<TeamManagement />} />
                  <Route path="users" element={<UsersManager />} />
                  <Route path="yahrzeit" element={<YahrzeitManager />} />
                  <Route path="times" element={<TimesManagerPage />} />
                  <Route path="zmanim" element={<ZmanimPage />} />
                </Route>
                
                {/* Logout route */}
                <Route path="/logout" element={<Navigate to="/login" replace />} />
                
                {/* Admin Panel route */}
                <Route 
                  path="/admin" 
                  element={
                    <RoleProtectedRoute>
                      <PageProvider>
                        <AdminPanel />
                      </PageProvider>
                    </RoleProtectedRoute>
                  } 
                />
                
                {/* Super admin routes */}
                <Route 
                  path="/admin/dashboard" 
                  element={
                    <RoleProtectedRoute>
                      <PageProvider>
                        <SuperUserDashboard />
                      </PageProvider>
                    </RoleProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/users" 
                  element={
                    <RoleProtectedRoute>
                      <PageProvider>
                        <UsersAdmin />
                      </PageProvider>
                    </RoleProtectedRoute>
                  } 
                />
                
                {/* Display Board - for main screen display */}
                <Route 
                  path="/display" 
                  element={
                    <RoleProtectedRoute>
                      <PageProvider>
                        <DisplayBoard />
                      </PageProvider>
                    </RoleProtectedRoute>
                  } 
                />
              </Routes>
            </QueryClientProvider>
          </SettingsProvider>
        </LanguageProvider>
      </AuthProvider>
    </SupabaseProvider>
  );
}

export default App;
